import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, MapPin } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const PrivacyPolicy: React.FC = () => {
  useSEO({
    title: 'Privacybeleid | WFT Study Buddy',
    description: 'Privacybeleid van WFT Study Buddy. Lees hoe wij omgaan met je persoonsgegevens en privacy.',
    canonicalUrl: '/privacy',
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Terug naar home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="glass-card rounded-2xl p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Privacybeleid</h1>
              <p className="text-gray-500 mt-1" suppressHydrationWarning>Laatst bijgewerkt: {new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>

          {/* Introduction */}
          <section className="mb-10">
            <p className="text-gray-700 leading-relaxed">
              WFT Study Buddy ("wij", "ons" of "onze") respecteert de privacy van alle gebruikers van onze website 
              en diensten. Wij dragen er zorg voor dat de persoonlijke informatie die u ons verschaft vertrouwelijk 
              wordt behandeld. Dit privacybeleid is van toepassing op de website wft-study-buddy.nl en alle 
              gerelateerde diensten.
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">1</span>
              Welke gegevens verzamelen wij?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>Wij verzamelen de volgende persoonsgegevens:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Accountgegevens:</strong> E-mailadres, naam (optioneel), wachtwoord (versleuteld)</li>
                <li><strong>Gebruiksgegevens:</strong> Studieresultaten, voortgang, quiz scores</li>
                <li><strong>Technische gegevens:</strong> IP-adres, browsertype, apparaatinformatie</li>
                <li><strong>Betalingsgegevens:</strong> Bij aankopen worden betalingen verwerkt via Stripe. Wij slaan geen creditcardgegevens op.</li>
              </ul>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">2</span>
              Waarom verzamelen wij deze gegevens?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>Wij gebruiken uw gegevens voor:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Het aanmaken en beheren van uw account</li>
                <li>Het leveren van onze studiediensten en het bijhouden van uw voortgang</li>
                <li>Het verwerken van betalingen</li>
                <li>Het verbeteren van onze diensten</li>
                <li>Het verzenden van belangrijke service-updates (geen marketing zonder toestemming)</li>
                <li>Het analyseren van websitegebruik via Google Analytics 4</li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">3</span>
              Rechtsgrond voor verwerking
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>Wij verwerken uw gegevens op basis van:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Uitvoering van de overeenkomst:</strong> Om onze diensten aan u te kunnen leveren</li>
                <li><strong>Toestemming:</strong> Voor het plaatsen van analytische cookies</li>
                <li><strong>Gerechtvaardigd belang:</strong> Voor het verbeteren van onze diensten</li>
                <li><strong>Wettelijke verplichting:</strong> Voor het bewaren van financiële administratie</li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">4</span>
              Delen met derden
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>Wij delen uw gegevens alleen met de volgende partijen:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Firebase (Google):</strong> Voor authenticatie en database opslag</li>
                <li><strong>Stripe:</strong> Voor betalingsverwerking</li>
                <li><strong>Google Analytics:</strong> Voor website-analyse (geanonimiseerd na consent)</li>
                <li><strong>Google Cloud:</strong> Voor hosting van onze diensten</li>
              </ul>
              <p className="mt-4">
                Wij verkopen uw gegevens nooit aan derden. Alle verwerkers zijn AVG-compliant en hebben 
                verwerkersovereenkomsten met ons gesloten.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">5</span>
              Bewaartermijnen
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Accountgegevens:</strong> Tot 2 jaar na laatste activiteit of tot u uw account verwijdert</li>
                <li><strong>Studieresultaten:</strong> Zolang uw account actief is</li>
                <li><strong>Betalingsgegevens:</strong> 7 jaar (wettelijke bewaarplicht)</li>
                <li><strong>Analytische gegevens:</strong> 26 maanden (Google Analytics standaard)</li>
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">6</span>
              Uw rechten
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>Onder de AVG heeft u de volgende rechten:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Inzage:</strong> U kunt opvragen welke gegevens wij van u hebben</li>
                <li><strong>Rectificatie:</strong> U kunt onjuiste gegevens laten corrigeren</li>
                <li><strong>Verwijdering:</strong> U kunt vragen om verwijdering van uw gegevens</li>
                <li><strong>Beperking:</strong> U kunt de verwerking laten beperken</li>
                <li><strong>Overdraagbaarheid:</strong> U kunt uw gegevens in een standaardformaat ontvangen</li>
                <li><strong>Bezwaar:</strong> U kunt bezwaar maken tegen bepaalde verwerkingen</li>
              </ul>
              <p className="mt-4">
                Om uw rechten uit te oefenen, neem contact met ons op via onderstaande gegevens.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">7</span>
              Cookies
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Wij gebruiken cookies om onze website te laten functioneren en om inzicht te krijgen in het 
                gebruik van onze website. Voor meer informatie over ons cookiebeleid, zie onze{' '}
                <Link to="/cookie-policy" className="text-blue-600 hover:text-blue-800 underline">
                  Cookieverklaring
                </Link>.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">8</span>
              Beveiliging
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Wij nemen de bescherming van uw gegevens serieus en nemen passende maatregelen om misbruik, 
                verlies, onbevoegde toegang en ongewenste openbaarmaking tegen te gaan. Dit omvat:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>SSL/TLS-versleuteling voor alle dataverkeer</li>
                <li>Versleutelde opslag van wachtwoorden</li>
                <li>Regelmatige beveiligingsupdates</li>
                <li>Beperkte toegang tot persoonsgegevens</li>
              </ul>
            </div>
          </section>

          {/* Section 9 - Contact */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">9</span>
              Contact
            </h2>
            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <p className="text-gray-700 font-medium">WFT Study Buddy</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-600">
                  <Mail className="w-5 h-5 text-blue-500" />
                  <a href="mailto:info@wft-study-buddy.nl" className="hover:text-blue-600">
                    info@wft-study-buddy.nl
                  </a>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  <span>Nederland</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm mt-4">
                Als u een klacht heeft over de verwerking van uw persoonsgegevens, kunt u ook een klacht 
                indienen bij de Autoriteit Persoonsgegevens via{' '}
                <a 
                  href="https://www.autoriteitpersoonsgegevens.nl" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  autoriteitpersoonsgegevens.nl
                </a>.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center text-sm font-bold">10</span>
              Wijzigingen
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Wij behouden ons het recht voor om dit privacybeleid te wijzigen. Wijzigingen worden op 
                deze pagina gepubliceerd. Bij belangrijke wijzigingen informeren wij u via e-mail.
              </p>
            </div>
          </section>
        </div>

        {/* Back to top */}
        <div className="text-center mt-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-xl text-gray-700 hover:bg-white hover:shadow-lg transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            Terug naar home
          </Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;